import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAdminData } from '../../admin/AdminDataContext';
import type { LayerGroup } from '../../admin/types';
import { useMapContext } from '../../map/MapContext';
import { geometryKind, type GeometryKind, type LayerConfig } from '../../map/demoLayers';
import { dictionariesApi, type DictionaryValue } from '../../lib/dictionariesApi';
import { layersApi, type LayerFieldDetail } from '../../lib/layersApi';
import { canManageLayers } from '../../lib/tokens';

const groupColorPalette = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#06b6d4', '#475569', '#db2777'];

/** Same geometry_type vocabulary the drawing tool accepts (see MapContext's OL_DRAW_TYPE) —
 * used here only to decide whether "Нарисовать объект" makes sense for a layer. */
const DRAWABLE_GEOMETRY_TYPES = new Set(['point', 'multipoint', 'line', 'multiline', 'polygon', 'multipolygon']);

const typeFilterOptions: { value: GeometryKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Все типы' },
  { value: 'point', label: 'Точка' },
  { value: 'line', label: 'Линия' },
  { value: 'polygon', label: 'Полигон' },
];

export function LayersDrawer({
  open,
  onToggle,
  onLayersChanged,
}: {
  open: boolean;
  onToggle: () => void;
  onLayersChanged?: () => void;
}) {
  const { layers, layerVisibility, layerOpacity, setLayerVisible, setLayerOpacity } = useMapContext();
  const { layerGroups, addLayerGroup } = useAdminData();
  const activeCount = layers.filter((l) => layerVisibility[l.id]).length;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState(groupColorPalette[0]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<GeometryKind | 'all'>('all');
  const [groupFilter, setGroupFilter] = useState<'all' | 'ungrouped' | string>('all');
  const [nameSearch, setNameSearch] = useState('');
  const filtersActive = typeFilter !== 'all' || groupFilter !== 'all' || nameSearch.trim() !== '';

  const resetFilters = () => {
    setTypeFilter('all');
    setGroupFilter('all');
    setNameSearch('');
  };

  const filteredLayers = useMemo(() => {
    // .trim() on both sides of the lookup — a group name saved with stray
    // whitespace (see main.py's _fix_layer_group_names) used to never match any
    // layer's settings.group string, silently leaving every layer "ungrouped".
    const byName = new Map(layerGroups.map((g) => [g.name.trim(), g]));
    return layers.filter((l) => {
      if (typeFilter !== 'all' && geometryKind(l.geometryType) !== typeFilter) return false;
      if (nameSearch.trim() && !l.name.toLowerCase().includes(nameSearch.trim().toLowerCase())) return false;
      if (groupFilter === 'ungrouped') {
        if (l.group && byName.has(l.group.trim())) return false;
      } else if (groupFilter !== 'all') {
        const g = byName.get((l.group ?? '').trim());
        if (!g || g.id !== groupFilter) return false;
      }
      return true;
    });
  }, [layers, layerGroups, typeFilter, nameSearch, groupFilter]);

  const roots = useMemo(
    () => layerGroups.filter((g) => g.parentId === null).sort((a, b) => a.sortOrder - b.sortOrder),
    [layerGroups],
  );
  const childrenByParent = useMemo(() => {
    const map = new Map<string, LayerGroup[]>();
    for (const g of layerGroups) {
      if (g.parentId === null) continue;
      const arr = map.get(g.parentId) ?? [];
      arr.push(g);
      map.set(g.parentId, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [layerGroups]);

  const { byGroupId, ungrouped } = useMemo(() => {
    const byName = new Map(layerGroups.map((g) => [g.name.trim(), g]));
    const byGroupId = new Map<string, LayerConfig[]>();
    const ungrouped: LayerConfig[] = [];
    for (const l of filteredLayers) {
      const g = l.group ? byName.get(l.group.trim()) : undefined;
      if (g) {
        const arr = byGroupId.get(g.id) ?? [];
        arr.push(l);
        byGroupId.set(g.id, arr);
      } else {
        ungrouped.push(l);
      }
    }
    return { byGroupId, ungrouped };
  }, [filteredLayers, layerGroups]);

  const hasContent = (group: LayerGroup): boolean =>
    (byGroupId.get(group.id)?.length ?? 0) > 0 || (childrenByParent.get(group.id) ?? []).some(hasContent);

  const isExpanded = (group: LayerGroup) => expanded[group.id] ?? group.defaultExpanded;
  const toggleExpanded = (group: LayerGroup) => setExpanded((prev) => ({ ...prev, [group.id]: !isExpanded(group) }));

  const toggleGroupVisibility = (groupLayers: LayerConfig[]) => {
    const allVisible = groupLayers.every((l) => layerVisibility[l.id]);
    for (const l of groupLayers) setLayerVisible(l.id, !allVisible);
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    addLayerGroup({
      name: newGroupName.trim(),
      color: newGroupColor,
      parentId: null,
      defaultExpanded: true,
      defaultVisible: true,
      visibilityMode: 'independent',
      minZoom: null,
      maxZoom: null,
    }).catch(() => {});
    setNewGroupName('');
    setNewGroupColor(groupColorPalette[0]);
    setCreatingGroup(false);
  };

  const handleMoveToGroup = async (layer: LayerConfig, groupName: string | null) => {
    await layersApi.setLayerGroup(layer.id, groupName);
    onLayersChanged?.();
  };

  const renderGroupNode = (group: LayerGroup, depth: number) => {
    const groupLayers = byGroupId.get(group.id) ?? [];
    const children = (childrenByParent.get(group.id) ?? []).filter((c) => !filtersActive || hasContent(c));
    const expandedNode = isExpanded(group);
    const allVisible = groupLayers.length > 0 && groupLayers.every((l) => layerVisibility[l.id]);
    return (
      <div key={group.id} style={{ marginLeft: depth * 14 }}>
        <div className="flex items-center gap-1 rounded-lg pl-1 pr-0.5 hover:bg-bg-surface-secondary">
          <button
            type="button"
            onClick={() => toggleExpanded(group)}
            className="flex flex-1 items-center gap-2 py-1.5 text-left"
          >
            <span className="w-3 shrink-0 text-center text-[10px] text-text-secondary">{expandedNode ? '▾' : '▸'}</span>
            <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
            <span className="flex-1 truncate text-xs font-semibold text-text-primary">{group.name}</span>
            <span className="text-[10px] font-medium text-text-secondary">{groupLayers.length}</span>
          </button>
          {groupLayers.length > 0 && (
            <button
              type="button"
              onClick={() => toggleGroupVisibility(groupLayers)}
              title={allVisible ? 'Скрыть все слои группы' : 'Показать все слои группы'}
              className="flex size-6 shrink-0 items-center justify-center rounded text-text-secondary hover:bg-bg-surface"
            >
              {allVisible ? '👁' : '⊘'}
            </button>
          )}
        </div>
        {expandedNode && (
          <div className="mt-2 flex flex-col gap-3 pl-5">
            {groupLayers.length === 0 && children.length === 0 ? (
              <p className="text-[11px] text-text-secondary">
                {filtersActive ? 'Ничего не найдено по текущим фильтрам' : 'Слоёв в группе пока нет'}
              </p>
            ) : (
              <>
                {groupLayers.map((layer) => (
                  <LayerRow
                    key={layer.id}
                    layer={layer}
                    visible={layerVisibility[layer.id]}
                    opacity={layerOpacity[layer.id] ?? 1}
                    groups={layerGroups}
                    onToggleVisible={() => setLayerVisible(layer.id, !layerVisibility[layer.id])}
                    onOpacityChange={(v) => setLayerOpacity(layer.id, v)}
                    onMoveToGroup={(groupName) => handleMoveToGroup(layer, groupName)}
                  />
                ))}
                {children.length > 0 && (
                  <div className="flex flex-col gap-4">{children.map((child) => renderGroupNode(child, depth + 1))}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title="Слои и фильтры"
        className="absolute left-6 top-6 z-10 flex size-11 items-center justify-center rounded-xl border border-border-default bg-bg-surface text-lg text-text-primary shadow-sm"
      >
        ☰
      </button>
    );
  }

  return (
    // z-20, not z-10 — on a narrow screen this panel can get close to full
    // width and visually reach under MapControls' icon column on the right;
    // at equal z-index MapControls (mounted after this in MapPage) would win
    // that overlap and sit on top of it, including right over the ☰ close
    // button in some cases.
    <div className="absolute left-6 top-6 z-20 flex max-h-[calc(100vh-120px)] w-[min(420px,calc(100vw-48px))] flex-col overflow-y-auto rounded-2xl border border-border-default bg-bg-surface p-5 shadow-lg">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">Слои и фильтры</h2>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium text-action-primary">{activeCount} активных</span>
          <button
            type="button"
            onClick={onToggle}
            title="Свернуть"
            className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary"
          >
            ☰
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Link
          to="/app/data/add"
          state={{ returnTo: '/app' }}
          className="flex h-10 flex-1 items-center justify-center rounded-[10px] border border-dashed border-border-strong text-[13px] font-medium text-action-primary hover:bg-bg-surface-secondary"
        >
          + Добавить слой
        </Link>
        {canManageLayers() && (
          <button
            type="button"
            onClick={() => setCreatingGroup((v) => !v)}
            className="flex h-10 items-center justify-center rounded-[10px] border border-border-default px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            + Группа
          </button>
        )}
      </div>

      {canManageLayers() && creatingGroup && (
        <div className="mt-3 rounded-xl border border-border-default bg-bg-surface-secondary p-3">
          <label className="text-[10px] font-medium text-text-secondary">Название группы</label>
          <input
            autoFocus
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
            placeholder="Например, Инженерные сети"
            className="mt-1 h-9 w-full rounded-lg border border-border-default bg-bg-surface px-2.5 text-xs text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
          />
          <div className="mt-2 flex items-center gap-1.5">
            {groupColorPalette.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setNewGroupColor(color)}
                className={`size-6 rounded-full border-2 ${newGroupColor === color ? 'border-text-primary' : 'border-transparent'}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim()}
              className="flex h-8 flex-1 items-center justify-center rounded-lg bg-action-primary text-[12px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              Создать
            </button>
            <button
              type="button"
              onClick={() => setCreatingGroup(false)}
              className="flex h-8 flex-1 items-center justify-center rounded-lg border border-border-default text-[12px] font-medium text-text-primary hover:bg-bg-surface"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setFiltersOpen((v) => !v)}
        className="mt-3 flex w-full items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-bg-surface-secondary"
      >
        <span className="w-3 shrink-0 text-center text-[10px] text-text-secondary">{filtersOpen ? '▾' : '▸'}</span>
        <span className="flex-1 text-left text-xs font-semibold text-text-secondary">Фильтры объектов</span>
        {filtersActive && <span className="size-1.5 shrink-0 rounded-full bg-action-primary" />}
      </button>
      {filtersOpen && (
        <div className="mt-2 flex flex-col gap-2">
          {filtersActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="self-start text-[11px] font-medium text-action-primary hover:underline"
            >
              Сбросить
            </button>
          )}
          <input
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="Поиск по названию слоя"
            className="h-11 rounded-lg bg-bg-canvas px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
          />
          <FilterSelect
            label="Тип геометрии"
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as GeometryKind | 'all')}
            options={typeFilterOptions}
          />
          <FilterSelect
            label="Группа"
            value={groupFilter}
            onChange={setGroupFilter}
            options={[
              { value: 'all', label: 'Все группы' },
              { value: 'ungrouped', label: 'Без группы' },
              ...layerGroups.map((g) => ({ value: g.id, label: g.name })),
            ]}
          />
        </div>
      )}

      {layers.length === 0 ? (
        <div className="mt-4 rounded-xl bg-bg-surface-secondary p-4 text-center">
          <p className="text-[13px] font-medium text-text-primary">На карте пока нет слоёв</p>
          <p className="mt-1 text-[11px] text-text-secondary">
            Нажмите «+ Добавить слой», чтобы подключить первый источник данных
          </p>
        </div>
      ) : filteredLayers.length === 0 ? (
        <div className="mt-4 rounded-xl bg-bg-surface-secondary p-4 text-center">
          <p className="text-[13px] font-medium text-text-primary">Ничего не найдено по текущим фильтрам</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-2 text-[11px] font-medium text-action-primary hover:underline"
          >
            Сбросить фильтры
          </button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          {roots.filter((g) => !filtersActive || hasContent(g)).map((group) => renderGroupNode(group, 0))}

          {ungrouped.length > 0 && (
            <div>
              {roots.length > 0 && (
                <div className="flex items-center gap-1 pl-1">
                  <p className="flex-1 text-xs font-semibold text-text-secondary">Без группы</p>
                  <button
                    type="button"
                    onClick={() => toggleGroupVisibility(ungrouped)}
                    title={
                      ungrouped.every((l) => layerVisibility[l.id]) ? 'Скрыть все слои без группы' : 'Показать все слои без группы'
                    }
                    className="flex size-6 shrink-0 items-center justify-center rounded text-text-secondary hover:bg-bg-surface-secondary"
                  >
                    {ungrouped.every((l) => layerVisibility[l.id]) ? '👁' : '⊘'}
                  </button>
                </div>
              )}
              <div className={`flex flex-col gap-3 ${roots.length > 0 ? 'mt-2' : ''}`}>
                {ungrouped.map((layer) => (
                  <LayerRow
                    key={layer.id}
                    layer={layer}
                    visible={layerVisibility[layer.id]}
                    opacity={layerOpacity[layer.id] ?? 1}
                    groups={layerGroups}
                    onToggleVisible={() => setLayerVisible(layer.id, !layerVisibility[layer.id])}
                    onOpacityChange={(v) => setLayerOpacity(layer.id, v)}
                    onMoveToGroup={(groupName) => handleMoveToGroup(layer, groupName)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LayerRow({
  layer,
  visible,
  opacity,
  groups,
  onToggleVisible,
  onOpacityChange,
  onMoveToGroup,
}: {
  layer: LayerConfig;
  visible: boolean;
  opacity: number;
  groups: LayerGroup[];
  onToggleVisible: () => void;
  onOpacityChange: (value: number) => void;
  onMoveToGroup: (groupName: string | null) => Promise<void>;
}) {
  const { openLayerTable, startDraw, fitToLayerExtent, attributeFilters, setAttributeFilter } = useMapContext();
  const canZoomToExtent = !layer.wms && !layer.wmts && !layer.raster;
  const { mapSettings } = useAdminData();
  const canDraw =
    mapSettings?.tools.editFeatures !== false &&
    Boolean(layer.canEditFeatures) &&
    Boolean(layer.geometryType && DRAWABLE_GEOMETRY_TYPES.has(layer.geometryType));
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupSubmenuOpen, setGroupSubmenuOpen] = useState(false);
  const [filterSubmenuOpen, setFilterSubmenuOpen] = useState(false);
  const [filterFieldCode, setFilterFieldCode] = useState<string | null>(null);
  const [dictValuesByField, setDictValuesByField] = useState<Record<string, DictionaryValue[]>>({});
  const [moving, setMoving] = useState(false);

  // Только dictionary-поля, помеченные фильтруемыми (gis.layer_fields.
  // is_filterable) — у строковых полей нет контролируемого набора значений
  // (это неоднородный свободный текст), для них уже есть поиск по объектам
  // слоя; предлагать фильтр по значению имеет смысл только там, где значения
  // это заранее известный список (справочник).
  const filterableFields = (layer.fields ?? []).filter((f) => f.filterable && f.data_type === 'dictionary');
  const activeFilter = attributeFilters[layer.id] ?? null;
  const activeFilterField = activeFilter ? filterableFields.find((f) => f.code === activeFilter.fieldCode) : undefined;

  useEffect(() => {
    if (activeFilterField?.dictionary_id && !dictValuesByField[activeFilterField.code]) {
      const code = activeFilterField.code;
      dictionariesApi
        .listValues(activeFilterField.dictionary_id)
        .then((vals) => setDictValuesByField((prev) => ({ ...prev, [code]: vals })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterField?.code]);

  const openFieldValues = (field: LayerFieldDetail) => {
    setFilterFieldCode(field.code);
    if (field.dictionary_id && !dictValuesByField[field.code]) {
      const code = field.code;
      dictionariesApi
        .listValues(field.dictionary_id)
        .then((vals) => setDictValuesByField((prev) => ({ ...prev, [code]: vals })))
        .catch(() => {});
    }
  };

  const activeFieldValueOptions: { value: string; label: string }[] = (() => {
    const field = filterFieldCode ? filterableFields.find((f) => f.code === filterFieldCode) : undefined;
    if (!field) return [];
    return (dictValuesByField[field.code] ?? []).map((v) => ({ value: v.id, label: v.name }));
  })();

  const activeFilterValueLabel = (() => {
    if (!activeFilter || !activeFilterField) return activeFilter?.value ?? null;
    return (dictValuesByField[activeFilterField.code] ?? []).find((v) => v.id === activeFilter.value)?.name ?? activeFilter.value;
  })();
  const menuRef = useRef<HTMLDivElement>(null);
  // Панель меню рендерится порталом в document.body (см. ниже) — иначе она обрезается
  // overflow-y-auto контейнером панели «Слои и фильтры» и приходится скроллить панель,
  // чтобы её увидеть, вместо того чтобы она всплывала поверх всего.
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  const openMenu = () => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const closeMenu = () => {
      setMenuOpen(false);
      setGroupSubmenuOpen(false);
      setFilterSubmenuOpen(false);
      setFilterFieldCode(null);
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || menuPanelRef.current?.contains(target)) return;
      closeMenu();
    };
    // capture:true — событие скролла вложенного div не всплывает, но перехватывается
    // на фазе погружения, так что window его всё равно увидит. Но с тех пор как у
    // самой панели меню появился свой overflow-y-auto (см. maxHeight ниже), скролл
    // колесом ВНУТРИ неё тоже стал считаться "скроллом где-то на странице" и тут же
    // закрывал меню — исключаем события, чей target лежит внутри самой панели.
    const onScroll = (e: Event) => {
      const target = e.target as Node;
      if (menuPanelRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menuOpen]);

  const handlePick = async (groupName: string | null) => {
    setMoving(true);
    try {
      await onMoveToGroup(groupName);
    } finally {
      setMoving(false);
      setMenuOpen(false);
      setGroupSubmenuOpen(false);
    }
  };

  return (
    <div
      className={`rounded-xl border p-3 ${layer.warning ? 'border-status-warning' : 'border-border-default'} bg-bg-surface`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleVisible}
          className={`flex size-[22px] shrink-0 items-center justify-center rounded-[6px] border text-[13px] font-semibold ${
            visible
              ? 'border-action-primary bg-action-primary text-text-inverse'
              : 'border-border-default bg-bg-surface-secondary text-text-secondary'
          }`}
        >
          {visible ? '✓' : '–'}
        </button>
        <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: layer.color }} />
        <div className="min-w-0 flex-1">
          <p className={`truncate text-[13px] font-medium ${visible ? 'text-text-primary' : 'text-text-secondary'}`}>
            {layer.name}
          </p>
          <p className={`text-[11px] ${layer.warning ? 'text-status-danger' : 'text-text-secondary'}`}>
            {layer.warning ?? `${layer.count.toLocaleString('ru-RU')} объектов`}
          </p>
          {activeFilter && (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-action-primary">
              <span className="truncate">
                Фильтр: {activeFilterField?.name ?? activeFilter.fieldCode} = {activeFilterValueLabel}
              </span>
              <button
                type="button"
                onClick={() => setAttributeFilter(layer.id, null)}
                title="Сбросить фильтр"
                className="shrink-0 text-text-secondary hover:text-status-danger"
              >
                ×
              </button>
            </p>
          )}
        </div>
        {layer.isReal && (
          <div ref={menuRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
              title="Действия со слоем"
              disabled={moving}
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary disabled:opacity-50"
            >
              {moving ? '…' : '⋮'}
            </button>
            {menuOpen &&
              menuPos &&
              createPortal(
                <div
                  ref={menuPanelRef}
                  style={{
                    position: 'fixed',
                    top: menuPos.top,
                    right: menuPos.right,
                    // Without a cap this panel (esp. with "Фильтр по полю"
                    // expanded, showing every field + every value) could run
                    // past the bottom of the viewport with no way to reach
                    // what fell below the fold — happened whenever the ⋮
                    // button itself sat low on screen (e.g. a layer near the
                    // bottom of a long, scrolled layer list).
                    maxHeight: `calc(100vh - ${menuPos.top}px - 16px)`,
                  }}
                  className="z-50 w-52 overflow-y-auto rounded-xl border border-border-default bg-bg-surface p-1.5 shadow-lg"
                >
                {canZoomToExtent && (
                  <button
                    type="button"
                    onClick={() => {
                      fitToLayerExtent(layer.id);
                      setMenuOpen(false);
                    }}
                    className="flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    Приблизить к слою
                  </button>
                )}
                {canManageLayers() && (
                  <Link
                    to={`/app/data/edit/${layer.id}`}
                    className="flex h-10 items-center rounded-lg px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    Редактирование
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    openLayerTable(layer.id, layer.name);
                    setMenuOpen(false);
                  }}
                  className="flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                >
                  Таблица
                </button>
                {canDraw && (
                  <button
                    type="button"
                    onClick={() => {
                      // Opens the session with plain click-to-select as the default —
                      // adding objects and box-select bulk actions are separate
                      // explicit toggles the user picks inside DrawModeBanner.
                      startDraw(layer.id);
                      setMenuOpen(false);
                    }}
                    className="flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    Редактирование объектов
                  </button>
                )}

                {filterableFields.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilterSubmenuOpen((v) => !v)}
                    className="flex h-10 w-full items-center justify-between rounded-lg px-3 text-left text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    <span>{activeFilter ? 'Изменить фильтр' : 'Фильтр по полю'}</span>
                    <span className="text-text-secondary">{filterSubmenuOpen ? '▾' : '▸'}</span>
                  </button>
                )}
                {filterSubmenuOpen && (
                  <div className="max-h-56 overflow-y-auto border-t border-border-default pt-1">
                    {activeFilter && (
                      <button
                        type="button"
                        onClick={() => {
                          setAttributeFilter(layer.id, null);
                          setFilterFieldCode(null);
                          setFilterSubmenuOpen(false);
                          setMenuOpen(false);
                        }}
                        className="flex h-9 w-full items-center rounded-lg px-3 text-left text-xs font-medium text-status-danger hover:bg-bg-surface-secondary"
                      >
                        Сбросить фильтр
                      </button>
                    )}
                    {!filterFieldCode ? (
                      filterableFields.map((f) => (
                        <button
                          key={f.code}
                          type="button"
                          onClick={() => openFieldValues(f)}
                          className="flex h-9 w-full items-center justify-between gap-2 rounded-lg px-3 text-left text-xs font-medium text-text-primary hover:bg-bg-surface-secondary"
                        >
                          <span className="truncate">{f.name}</span>
                          <span className="shrink-0 text-text-secondary">▸</span>
                        </button>
                      ))
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setFilterFieldCode(null)}
                          className="flex h-8 w-full items-center rounded-lg px-3 text-left text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary"
                        >
                          ← назад
                        </button>
                        {activeFieldValueOptions.length === 0 ? (
                          <p className="px-3 py-2 text-[11px] text-text-secondary">Нет значений</p>
                        ) : (
                          activeFieldValueOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setAttributeFilter(layer.id, { fieldCode: filterFieldCode, value: opt.value });
                                setFilterSubmenuOpen(false);
                                setFilterFieldCode(null);
                                setMenuOpen(false);
                              }}
                              className="flex h-9 w-full items-center truncate rounded-lg px-3 text-left text-xs font-medium text-text-primary hover:bg-bg-surface-secondary"
                            >
                              {opt.label}
                            </button>
                          ))
                        )}
                      </>
                    )}
                  </div>
                )}

                {canManageLayers() && (
                  <button
                    type="button"
                    onClick={() => setGroupSubmenuOpen((v) => !v)}
                    className="flex h-10 w-full items-center justify-between rounded-lg px-3 text-left text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    <span>Переместить в группу</span>
                    <span className="text-text-secondary">{groupSubmenuOpen ? '▾' : '▸'}</span>
                  </button>
                )}
                {canManageLayers() && groupSubmenuOpen && (
                  <div className="max-h-48 overflow-y-auto border-t border-border-default pt-1">
                    <button
                      type="button"
                      onClick={() => handlePick(null)}
                      className="flex h-9 w-full items-center rounded-lg px-3 text-left text-xs font-medium text-text-secondary hover:bg-bg-surface-secondary"
                    >
                      Без группы
                    </button>
                    {groups.length === 0 ? (
                      <p className="px-3 py-2 text-[11px] text-text-secondary">Групп ещё нет</p>
                    ) : (
                      groups.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => handlePick(g.name)}
                          className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-xs font-medium text-text-primary hover:bg-bg-surface-secondary"
                        >
                          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: g.color }} />
                          <span className="truncate">{g.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                </div>,
                document.body,
              )}
          </div>
        )}
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={opacity}
        disabled={!visible}
        onChange={(e) => onOpacityChange(Number(e.target.value))}
        className="mt-3 w-full accent-action-primary disabled:opacity-40"
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex h-11 flex-col justify-center rounded-lg bg-bg-canvas px-3">
      <span className="text-[9px] font-medium text-text-secondary">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="-ml-0.5 bg-transparent text-xs font-medium text-text-primary outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
